import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../api/axiosInstance";
import AuthLayout, { PasswordInput, SubmitButton, AuthAlert } from "../components/AuthLayout";

// Step 2 of forgot password: the user lands here from the email link
// (/reset-password/:token) and chooses a new password.
export default function ResetPassword() {
  const { token } = useParams(); // the token from the email link
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Quick checks in the browser so the user gets instant feedback.
    // The backend repeats these checks, because it never trusts the browser.
    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/user/reset-password/${token}`, { password, confirmPassword });
      setDone(true);

      // Send them to sign in after a short pause so they can read the message.
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err.response?.data?.message || "Could not reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

  if (done) {
    return (
      <AuthLayout
        title="Password updated"
        subtitle="You can now sign in with your new password. Taking you to the sign-in page..."
        footer={<Link to="/login">Go to sign in now</Link>}
      >
        <AuthAlert tone="success">Your password has been reset.</AuthAlert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Enter a new password for your account. You'll use it the next time you sign in."
      footer={
        <>
          Link expired? <Link to="/forgot-password">Request a new one</Link>
        </>
      }
    >
      {error && <AuthAlert tone="error">{error}</AuthAlert>}

      <form className="au-form" onSubmit={handleSubmit}>
        <div className="au-field">
          <label className="au-label" htmlFor="reset-password">New password</label>
          <PasswordInput
            id="reset-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            autoComplete="new-password"
          />
        </div>

        <div className="au-field">
          <label className="au-label" htmlFor="reset-confirm">Confirm new password</label>
          <PasswordInput
            id="reset-confirm"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter the new password"
            autoComplete="new-password"
          />
          {mismatch && <p className="au-hint au-hint-error">Passwords do not match.</p>}
        </div>

        <SubmitButton loading={loading} loadingText="Saving...">
          Reset password
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}